import { useState } from 'react';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { detectPlatform } from '@/lib/push-platform';
import { isGerman } from '@/i18n/locale';

// ════════════════════════════════════════════════════════════════════
// PushNotificationsToggle — Profile card for managing push state
// after the initial prompt has been answered (or skipped).
//
// Visibility rules:
//   - Hide entirely on 'unsupported' platforms (iOS Safari w/o PWA,
//     missing APIs). User can't act here, no point showing the surface.
//   - When Notification.permission === 'denied' (browser-level block),
//     show explainer-only card — no toggle. They must un-deny in
//     browser settings.
//   - Otherwise show toggle reflecting hook status.
//
// Disable confirmation uses window.confirm() — minimal, native, works
// across browsers. Backlog: replace with bottom-sheet pattern.
// ════════════════════════════════════════════════════════════════════

export function PushNotificationsToggle() {
  const de = isGerman();
  const [platform] = useState(() => detectPlatform());
  const { status, enable, disable } = usePushSubscription();
  const [busy, setBusy] = useState(false);

  if (platform === 'unsupported') return null;

  // Browser-level block — no actionable toggle.
  const blocked =
    typeof Notification !== 'undefined' && Notification.permission === 'denied';

  const onEnable = async () => {
    setBusy(true);
    try {
      await enable();
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    const message = de
      ? 'Push-Erinnerungen deaktivieren?'
      : 'Disable push reminders?';
    if (!window.confirm(message)) return;
    setBusy(true);
    try {
      await disable();
    } finally {
      setBusy(false);
    }
  };

  const isEnabled = status === 'enabled';
  const loading = status === 'loading' || busy;

  return (
    <section className="card p-5">
      <h2 className="font-display font-semibold text-slate">
        {de ? 'Erinnerungen' : 'Reminders'}
      </h2>
      <p className="text-xs text-slate-muted mt-1 leading-relaxed">
        {de
          ? 'Push-Erinnerungen für Termine, Impfungen und Entwicklungstipps.'
          : 'Push reminders for appointments, vaccinations and development tips.'}
      </p>

      {blocked ? (
        <p className="mt-4 text-sm text-slate-muted leading-relaxed">
          {de
            ? 'Push wurde im Browser blockiert. Bitte in den Browser-Einstellungen freigeben.'
            : 'Push was blocked in the browser. Please re-enable it in browser settings.'}
        </p>
      ) : (
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-sm text-slate" aria-live="polite">
            {isEnabled
              ? (de ? 'Push-Erinnerungen sind aktiv' : 'Push reminders are active')
              : (de ? 'Aktivieren um Erinnerungen zu bekommen' : 'Enable to receive reminders')}
          </span>
          <button
            type="button"
            role="switch"
            onClick={isEnabled ? onDisable : onEnable}
            disabled={loading}
            aria-checked={isEnabled}
            aria-busy={loading || undefined}
            aria-label={de ? 'Push-Erinnerungen' : 'Push reminders'}
            className={
              'shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 disabled:cursor-wait ' +
              (isEnabled ? 'bg-primary' : 'bg-primary-200')
            }
          >
            <span
              className={
                'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ' +
                (isEnabled ? 'translate-x-5' : 'translate-x-0.5')
              }
              aria-hidden
            />
          </button>
        </div>
      )}
    </section>
  );
}
